import { useCallback, useEffect, useState } from 'react';
import { request } from '@/lib/api.js';
import { useFmt } from '@/lib/useFmt.js';
import { describeAudit, TONE_CLASSES } from '@/lib/auditDescriptors.js';
import { Chip, PageHeader, Pagination } from '@/components/ui/index.js';
import type { AuditLogItem, AuditLogsResponse } from '@/lib/types.js';

const PAGE_SIZE = 50;

export function AuditLogsPage(): JSX.Element {
  const [data, setData] = useState<AuditLogsResponse | null>(null);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<'all' | 'user' | 'admin' | 'failed'>('all');
  const [sinceDays, setSinceDays] = useState<0 | 1 | 7 | 30>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { fmtDateTime, fmtDate } = useFmt();

  const load = useCallback(async (
    offsetValue: number,
    filterValue: typeof filter,
    sinceDaysValue: typeof sinceDays,
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offsetValue),
      });
      if (filterValue === 'user') params.set('action_prefix', 'user.');
      if (filterValue === 'admin') params.set('action_prefix', 'admin.');
      if (filterValue === 'failed') params.set('action_prefix', 'user.login_failed');
      if (sinceDaysValue > 0) params.set('since_days', String(sinceDaysValue));
      const res = await request<AuditLogsResponse>(`/admin/audit_logs?${params.toString()}`);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(offset, filter, sinceDays);
  }, [offset, filter, sinceDays, load]);

  return (
    <div>
      <PageHeader title="审计日志" subtitle="用户 / 管理员 / 系统 webhook 所有操作的事件流。" />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-slate-500 mr-1">类型</span>
        {(['all', 'user', 'admin', 'failed'] as const).map((f) => (
          <Chip
            key={f}
            active={filter === f}
            onClick={() => { setFilter(f); setOffset(0); }}
          >
            {f === 'all' ? '全部' : f === 'user' ? '用户操作' : f === 'admin' ? '管理员操作' : '登录失败'}
          </Chip>
        ))}
        <span className="sm:ml-auto text-sm text-slate-400">共 {data?.total ?? 0}</span>
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-slate-500 mr-1">时间范围</span>
        {SINCE_OPTIONS.map((s) => (
          <Chip
            key={s.value}
            active={sinceDays === s.value}
            onClick={(): void => { setSinceDays(s.value); setOffset(0); }}
          >
            {s.label}
          </Chip>
        ))}
      </div>

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="mat-matte rounded-xl overflow-hidden">
       <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/[0.04]">
            <tr>
              <th className="px-4 py-3 text-left text-slate-300 font-medium w-[160px]">时间</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">动作</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium w-[100px]">主体</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium w-[120px]">IP</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium w-[60px]">原始</th>
            </tr>
          </thead>
          <tbody>
            {loading && data === null ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">加载中…</td></tr>
            ) : data === null || data.logs.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">暂无记录</td></tr>
            ) : (
              data.logs.flatMap((log) => renderLogRow(log, expanded, setExpanded, fmtDateTime, fmtDate))
            )}
          </tbody>
        </table>
       </div>
      </div>

      <Pagination
        offset={offset}
        total={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onChange={setOffset}
      />
    </div>
  );
}

const SINCE_OPTIONS: readonly { value: 0 | 1 | 7 | 30; label: string }[] = [
  { value: 0, label: '全部' },
  { value: 1, label: '今天' },
  { value: 7, label: '7 天' },
  { value: 30, label: '30 天' },
] as const;

function renderLogRow(
  log: AuditLogItem,
  expanded: Set<string>,
  setExpanded: (next: Set<string>) => void,
  fmtDateTime: (s: string | null | undefined) => string,
  fmtDate: (s: string | null | undefined) => string,
): React.ReactElement[] {
  const desc = describeAudit(log.action, log.payload, { fmtDate });
  const isOpen = expanded.has(log.id);
  const hasPayload = log.payload !== null && Object.keys(log.payload).length > 0;

  function toggle(): void {
    const next = new Set(expanded);
    if (next.has(log.id)) next.delete(log.id);
    else next.add(log.id);
    setExpanded(next);
  }

  const rows = [
    <tr key={log.id} className="border-t border-white/[0.08] align-top">
      <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">
        {fmtDateTime(log.created_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-baseline gap-2">
          <span className="text-base shrink-0">{desc.emoji}</span>
          <span className={TONE_CLASSES[desc.tone]}>{desc.summary}</span>
        </div>
        <code className="block mt-0.5 text-[10px] text-slate-500 font-mono">
          {log.action}
        </code>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 font-mono">
        {log.user_id !== null ? log.user_id.slice(0, 8) : '—'}
      </td>
      <td className="px-4 py-3 text-slate-500 text-xs font-mono">{log.ip ?? '—'}</td>
      <td className="px-4 py-3">
        {hasPayload && (
          <button
            type="button"
            onClick={toggle}
            className="text-xs text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded border border-white/10 hover:border-white/10"
          >
            {isOpen ? '收起' : '展开'}
          </button>
        )}
      </td>
    </tr>,
  ];

  if (isOpen && hasPayload) {
    rows.push(
      <tr key={`${log.id}-payload`} className="border-t border-white/[0.08] bg-white/[0.03]">
        <td colSpan={5} className="px-4 py-3">
          <pre className="text-[11px] text-slate-400 whitespace-pre-wrap break-all">
            {JSON.stringify(log.payload, null, 2)}
          </pre>
        </td>
      </tr>,
    );
  }
  return rows;
}

